import { useRouter } from "next/router";
import { useCallback, useEffect, useRef } from "react";
import { toast } from "sonner";
import { mutate } from "swr";
import { useClearPolicyAutosaves } from "./policyAutosave";
import { UserData } from "./types";
import useData from "./useData";
import createPersistedState from "./createPersistedState";
import { useImpersonateParam } from "./useImpersonateParam";
import {
  APERTURE_SESSIONS_KEY,
  readApertureSessions,
} from "./useApertureSession";
import useTenant, { invalidateTenantCache } from "./useTenant";

type UseUserResult =
  | {
      user: UserData;
      isLoading: false;
      logout: () => Promise<void>;
      refresh: () => void;
    }
  | {
      user: null;
      isLoading: true;
      logout: () => Promise<void>;
      refresh: () => void;
    };

export const useUserLoggedIn = createPersistedState<boolean | null>(
  "user_logged_in"
);

const ME_ENDPOINT = "/web/me";
/** Clear the SWR cache for the current user. */
export function invalidateUserCache() {
  mutate(ME_ENDPOINT, undefined);
}

export function useOptionalUser() {
  const router = useRouter();
  const impersonateParam = useImpersonateParam();

  // Don't fetch until router is ready (impersonateParam is undefined while hydrating)
  // SWR skips fetch when the key function returns null/undefined
  const apiPath = () => {
    if (impersonateParam === undefined) {
      return null;
    }
    return impersonateParam
      ? `${ME_ENDPOINT}?impersonate=${encodeURIComponent(impersonateParam)}`
      : ME_ENDPOINT;
  };

  const { data: user, refresh, error } = useData<UserData>(apiPath);
  const clearPolicyAutosaves = useClearPolicyAutosaves();
  const [userLoggedIn, setUserLoggedIn] = useUserLoggedIn(false);

  useEffect(() => {
    if (user && !userLoggedIn) setUserLoggedIn(true);
  }, [user, userLoggedIn, setUserLoggedIn]);

  // Strip the impersonate param from URL once the backend confirms impersonation
  // (cookie is now set and will persist across navigation)
  useEffect(() => {
    if (user?.impersonated_by && router.query.impersonate) {
      const { impersonate, ...remainingQuery } = router.query;
      router.replace(
        { pathname: router.pathname, query: remainingQuery },
        undefined,
        { shallow: true }
      );
    }
  }, [user?.impersonated_by, router]);

  const logout = useCallback(async () => {
    await fetch("/web/logout");
    // Reset user and tenant state to undefined
    refresh(undefined);
    invalidateTenantCache();
    clearPolicyAutosaves();
    setUserLoggedIn(false);
    localStorage.removeItem(APERTURE_SESSIONS_KEY);
  }, [refresh, clearPolicyAutosaves]);

  return {
    user: user ? user : null,
    isLoading: !user && !error,
    logout,
    error,
    refresh,
    userLoggedIn,
  };
}

// Note: useUser is just like useOptionalUser, but redirects to / if user is
// unauthenticated
/**
 * Gets the current logged in user, or redirects to `/`
 *
 * @param force If true, then `user` should ALWAYS be set (this basically tells
 * typescript that you know what you're doing and there will be a user). If you
 * don't pass `force`, then `user` is possibly null (indicating a loading state)
 */
function useUser(force: true): {
  user: UserData;
  isLoading: false;
  logout: () => Promise<void>;
  refresh: () => void;
};
function useUser(): UseUserResult;
function useUser(_force?: boolean): UseUserResult {
  const { user, logout, error, refresh } = useOptionalUser();
  const { tenant } = useTenant();
  const impersonateParam = useImpersonateParam();
  const router = useRouter();

  // Capture tenant kind before it gets cleared by logout.
  // When logout() clears SWR caches, tenant becomes null — but the ref retains
  // the last known value, so the error handler can redirect appropriately.
  const lastTenantKindRef = useRef<string | null>(null);
  if (tenant) lastTenantKindRef.current = tenant.kind;

  // Guard against the error handler firing multiple times. Each logout()
  // clears SWR caches, SWR refetches /web/me, gets another 401, and the
  // effect re-runs — without this guard we get a cascade of logout + redirect calls.
  const redirectingRef = useRef(false);

  useEffect(() => {
    if (error) {
      console.warn("Error fetching current user:", error);

      if (error instanceof Response) {
        const stripImpersonateParam = () => {
          const { impersonate, ...remainingQuery } = router.query;
          router.replace({ pathname: router.pathname, query: remainingQuery });
        };

        // Handle impersonation errors (with URL param)
        if (impersonateParam) {
          if (error.status === 400) {
            toast.error("Cannot impersonate yourself", {
              position: "top-center",
            });
            stripImpersonateParam();
            return;
          }
          if (error.status === 404) {
            toast.error(`User not found: ${impersonateParam}`, {
              position: "top-center",
            });
            stripImpersonateParam();
            return;
          }
          if (error.status === 403) {
            toast.error("You do not have permission to impersonate users", {
              position: "top-center",
            });
            stripImpersonateParam();
            return;
          }
        }

        // Handle stale impersonation cookie errors (without URL param)
        // 403/404 without URL param could be a stale cookie - clear and reload
        if (
          !impersonateParam &&
          (error.status === 403 || error.status === 404)
        ) {
          console.warn(
            "Possible stale impersonation cookie, clearing and reloading"
          );
          fetch("/web/clear-impersonation", { method: "POST" }).then(() => {
            router.reload();
          });
          return;
        }
      }

      // Otherwise it's an auth error - logout and redirect.
      if (redirectingRef.current) return;
      redirectingRef.current = true;

      // Detect Aperture user: ref is set when tenant was loaded before logout;
      // localStorage survives page refresh when the ref hasn't been populated.
      const isApertureUser =
        lastTenantKindRef.current === "tailnet" ||
        Object.keys(readApertureSessions()).length > 0;

      if (!isApertureUser) {
        logout().then(() => router.replace("/"));
        return;
      }

      logout().then(() => router.replace("/aperture"));
    }
  }, [error, impersonateParam, logout, router]);

  if (!user) {
    return { user: null, isLoading: true, logout, refresh };
  } else {
    return { user, isLoading: false, logout, refresh };
  }
}

export default useUser;
